import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Trash2 } from "lucide-react";
import { Button } from "components/ui/button";
import routes from "routes/routes";
import {
  useAddOrganizationMembersMutation,
  useGetOrganizationMembersQuery,
  useRemoveOrganizationMemberMutation,
} from "api/account/accountApiSlice";
import { ContactInviteResult, ContactMemberRow } from "api/account/types";
import AccountPageLayout from "../components/AccountPageLayout";
import { ORGANIZATION_ID_PARAM } from "../common/organizationAdmin";
import { STATION_TIME_ZONE } from "util/stationTime";

/**
 * The roster of ONE organization, for its contact (#2163).
 *
 * Capabilities are deliberately three: see the roster, invite by e-mail,
 * remove. No booking-on-behalf, no reporting, no billing, no unit editing,
 * no editing the organization itself — each is a separate question, named
 * and deferred in the spec rather than absorbed here.
 *
 * This does not reintroduce self-claim (ADR-0021): nobody asks to join, no
 * organization is listed that the caller is not already the contact of, and
 * an invited address only becomes a membership once its owner proves the
 * mailbox by OTP. What widened is who may ISSUE an invitation.
 *
 * No CSV upload, unlike the admin surface: ADR-0009 makes this app PWA-first
 * and a file picker on a phone is the worst part of the flow. The paste box
 * covers the same "we hired twelve people" case with no file handling.
 */

/**
 * One line of the "last action" panel.
 *
 * A local UI type on purpose, rather than widening the backend's
 * `ContactInviteOutcome`: that enum answers "what did adding this address
 * do?", and a removal is a different operation on a different endpoint.
 * Folding them into one server type would put a value in the API that no
 * server path can ever return.
 */
type MemberAction =
  | { kind: "add"; email: string; outcome: string; at: number }
  | { kind: "removed"; email: string; at: number };

const OrganizationMembersPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const organizationId = String(params[ORGANIZATION_ID_PARAM] ?? "");

  const { data, isLoading, isError, error } = useGetOrganizationMembersQuery(
    { organizationId },
    { skip: organizationId.length === 0 }
  );
  const [addMembers, { isLoading: isAdding }] = useAddOrganizationMembersMutation();
  const [removeMember, { isLoading: isRemoving }] = useRemoveOrganizationMemberMutation();

  const [emailInput, setEmailInput] = React.useState("");
  const [formError, setFormError] = React.useState<string | null>(null);
  /**
   * The result of the LAST action on this roster — additions or a removal.
   *
   * It used to hold additions only, and removal left it untouched. So after
   * withdrawing an invitation the panel still read "Einladung gesendet": a
   * true statement about an earlier moment, displayed as if it were the
   * current one, right underneath a list the address had just vanished from
   * (PO, 2026-08-26). A stale status is worse than none — it is the only
   * thing on screen claiming to say what happened.
   *
   * Carries its own timestamp because "sent, then withdrawn" is a sequence,
   * and a bare label cannot say which of the two you are looking at.
   */
  const [lastAction, setLastAction] = React.useState<MemberAction[] | null>(null);
  const [pendingRemoveEmail, setPendingRemoveEmail] = React.useState<string | null>(null);

  // The organization is context, not form state: switching to another one
  // must not carry a half-typed invite list across (house rule — reset UI
  // state on a context switch).
  React.useEffect(() => {
    setEmailInput("");
    setFormError(null);
    setLastAction(null);
    setPendingRemoveEmail(null);
  }, [organizationId]);

  const members: ContactMemberRow[] = Array.isArray(data?.members) ? data!.members : [];

  const submit = async () => {
    setFormError(null);
    setLastAction(null);
    const emails = emailInput
      .split(/[\s,;]+/)
      .map((value) => value.trim())
      .filter((value) => value.length > 0);
    if (emails.length === 0) {
      setFormError(t("account.organization_admin.no_addresses"));
      return;
    }
    try {
      const results = await addMembers({ organizationId, emails }).unwrap();
      const at = Date.now();
      setLastAction(
        (Array.isArray(results) ? results : []).map((result: ContactInviteResult) => ({
          kind: "add" as const,
          email: result.email,
          outcome: result.outcome,
          at,
        })),
      );
      setEmailInput("");
    } catch (err) {
      const e = err as { data?: { message?: string } };
      setFormError(e?.data?.message ?? t("account.organization_admin.add_error"));
    }
  };

  const confirmRemove = async () => {
    if (!pendingRemoveEmail) return;
    setFormError(null);
    try {
      await removeMember({ organizationId, email: pendingRemoveEmail }).unwrap();
      // Replaces whatever the panel said, rather than leaving an earlier
      // outcome standing next to a row that has just disappeared.
      setLastAction([{ kind: "removed", email: pendingRemoveEmail, at: Date.now() }]);
      setPendingRemoveEmail(null);
    } catch (err) {
      const e = err as { data?: { message?: string; code?: string } };
      // A contact person is removed by the operator only (#3037) — say so in
      // the reader's language rather than relaying the server's English.
      setFormError(
        e?.data?.code === "is_contact"
          ? t("account.organization_admin.remove_contact_not_allowed")
          : e?.data?.message ?? t("account.organization_admin.remove_error")
      );
      setPendingRemoveEmail(null);
    }
  };

  const formatJoinedAt = (joinedAt: string | null) =>
    joinedAt
      ? new Date(joinedAt).toLocaleDateString(i18n.language, { timeZone: STATION_TIME_ZONE })
      : t("common.not_applicable");

  if (isLoading) {
    return (
      <AccountPageLayout title={t("account.organization_admin.members_title")}>
        <p className="text-sm text-slate-600">{t("common.loading")}</p>
      </AccountPageLayout>
    );
  }

  /**
   * A 404 here is not "empty" — it means the person no longer administers
   * this organization (the mark was cleared, the membership suspended, or the
   * organization archived between opening the page and loading it). Say that
   * plainly with a way back, never a generic failure and never a blank list
   * (#1926's revoked-mid-flow pattern; an error rendered as an empty state is
   * the #1579 failure).
   */
  if (isError || !data) {
    const status = (error as { status?: number } | undefined)?.status;
    return (
      <AccountPageLayout title={t("account.organization_admin.members_title")}>
        <p className="text-sm text-slate-600">
          {status === 404
            ? t("account.organization_admin.no_longer_yours")
            : t("account.organization_admin.load_error")}
        </p>
        <Button variant="outline" onClick={() => navigate(routes.accountPath)}>
          {t("account.organization_admin.back_to_account")}
        </Button>
      </AccountPageLayout>
    );
  }

  return (
    <AccountPageLayout title={data.organization.organizationName}>
      <p className="text-sm text-slate-600">{t("account.organization_admin.members_intro")}</p>

      {formError && (
        <div className="rounded-md border border-foreground/15 px-3 py-2 text-sm text-foreground">
          {formError}
        </div>
      )}

      {members.length === 0 ? (
        <p className="text-sm text-slate-600">{t("account.organization_admin.empty")}</p>
      ) : (
        <ul className="space-y-2">
          {members.map((member) => (
            <li
              key={`${member.state}-${member.email}`}
              className="flex items-start justify-between gap-3 rounded-md bg-primary/5 px-3 py-2"
            >
              <div className="min-w-0">
                <p className="truncate text-sm text-foreground">{member.name ?? member.email}</p>
                {member.name && (
                  <p className="truncate text-xs text-slate-600">{member.email}</p>
                )}
                <p className="text-xs text-slate-500">
                  {member.state === "invited"
                    ? t("account.organization_admin.state.invited")
                    : t("account.organization_admin.state.active", {
                        date: formatJoinedAt(member.joinedAt),
                      })}
                  {member.isContact && ` · ${t("account.organization_admin.state.contact")}`}
                </p>
              </div>
              {/* The contact cannot remove themselves — the server refuses it
                  (409) and the button is not offered, so the rule is explained
                  here and enforced there. */}
              {!member.isContact && (
                <button
                  type="button"
                  onClick={() => setPendingRemoveEmail(member.email)}
                  aria-label={t("account.organization_admin.remove")}
                  className="p-2 text-slate-500 transition-colors hover:text-foreground"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {pendingRemoveEmail && (
        <div className="space-y-3 rounded-md border border-foreground/15 px-3 py-3">
          {/* Says what removal actually does. The person clicking has no other
              way to know that the account and its own bookings survive. */}
          <p className="text-sm text-foreground">
            {t("account.organization_admin.remove_confirm", { email: pendingRemoveEmail })}
          </p>
          <p className="text-xs text-slate-600">
            {t("account.organization_admin.remove_consequence")}
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setPendingRemoveEmail(null)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={confirmRemove} disabled={isRemoving}>
              {isRemoving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("account.organization_admin.remove")}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700" htmlFor="org-invite-emails">
          {t("account.organization_admin.add_label")}
        </label>
        <textarea
          id="org-invite-emails"
          value={emailInput}
          onChange={(event) => setEmailInput(event.target.value)}
          placeholder={t("account.organization_admin.add_placeholder")}
          rows={3}
          className="w-full rounded-md border border-foreground/15 bg-white px-3 py-2 text-sm text-foreground focus:border-foreground/40 focus:outline-none"
        />
        <Button
          onClick={submit}
          disabled={isAdding || emailInput.trim().length === 0}
          className="w-full"
        >
          {isAdding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t("account.organization_admin.add_button")}
        </Button>
      </div>

      {lastAction && lastAction.length > 0 && (
        <div className="space-y-1">
          <p className="text-sm font-medium text-slate-700">
            {t("account.organization_admin.outcomes_title")}
          </p>
          <ul className="text-sm text-slate-600">
            {lastAction.map((entry) => (
              <li key={entry.email} className="flex justify-between gap-4 py-0.5">
                <span className="truncate">{entry.email}</span>
                <span className="shrink-0 text-slate-500">
                  {entry.kind === "removed"
                    ? t("account.organization_admin.outcome.removed")
                    : t(`account.organization_admin.outcome.${entry.outcome}`)}
                  {" · "}
                  {/* Time only, not a date: this panel reports what just
                      happened in this sitting, and a date would imply a
                      history it does not keep. */}
                  {new Date(entry.at).toLocaleTimeString(i18n.language, {
                    timeZone: STATION_TIME_ZONE,
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Button variant="outline" className="w-full" onClick={() => navigate(routes.accountPath)}>
        {t("account.organization_admin.back_to_account")}
      </Button>
    </AccountPageLayout>
  );
};

export default OrganizationMembersPage;
